import { renderInline } from "idfweb-workbench/markdown";

/**
 * The welcome window's page of examples: a search field over a tree of the
 * example sets. Each set is a folder, and inside it the examples are arranged
 * by the directories of their paths. Typing in the field replaces the tree
 * with a flat list of the examples that match.
 */

export interface ExampleSet {
    label: string;
    examples: () => Promise<PickerExample[]>;
}

export interface PickerExample {
    /** The example's directory within its set, e.g. "system/tracing/sysview_tracing". */
    path: string;
    title: string;
    /** Inline Markdown. */
    description: string;
    /** The chips it builds for. */
    detail?: string;
    open(): void;
}

export interface ExampleFolder {
    name: string;
    items: (ExampleFolder | PickerExample)[];
    /** The examples in this folder and below it. */
    count: number;
}

/** The examples arranged by the directories of their paths, each level sorted by name. */
export function exampleTree(name: string, examples: PickerExample[]): ExampleFolder {
    const root: ExampleFolder = { name, items: [], count: 0 };
    for (const example of examples) {
        const segments = example.path.split("/");
        let folder = root;
        folder.count++;
        for (const segment of segments.slice(0, -1)) {
            let next = folder.items.find((item): item is ExampleFolder => "items" in item && item.name === segment);
            if (next === undefined) folder.items.push((next = { name: segment, items: [], count: 0 }));
            next.count++;
            folder = next;
        }
        folder.items.push(example);
    }
    sortTree(root);
    return root;
}

function sortTree(folder: ExampleFolder): void {
    const key = (item: ExampleFolder | PickerExample) => ("items" in item ? item.name : item.path.split("/").at(-1)!);
    folder.items.sort((a, b) => (key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : 0));
    for (const item of folder.items) if ("items" in item) sortTree(item);
}

/** The examples whose path, title or description contains every word of the query. */
export function searchExamples(examples: PickerExample[], query: string): PickerExample[] {
    const words = query
        .toLowerCase()
        .split(/\s+/)
        .filter((word) => word !== "");
    return examples.filter((example) => {
        const text = `${example.path} ${example.title} ${example.description}`.toLowerCase();
        return words.every((word) => text.includes(word));
    });
}

/** The page's content. `close` closes the welcome window before an example is opened. */
export function renderExamplePicker(sets: ExampleSet[], close: () => void): HTMLElement {
    const page = document.createElement("div");
    page.className = "idfweb-picker";
    const search = document.createElement("input");
    search.type = "search";
    search.className = "idfweb-picker-search";
    search.placeholder = "Search examples…";
    search.setAttribute("aria-label", "Search examples");

    const loaded = sets.map((set) => set.examples());
    const tree = document.createElement("div");
    tree.className = "idfweb-welcome-list";
    sets.forEach((set, i) => {
        const folder = loaded[i]!.then((examples) => exampleTree(set.label, examples));
        tree.append(renderFolder(set.label, folder, close));
    });
    const results = document.createElement("div");
    results.className = "idfweb-welcome-list idfweb-picker-results";
    results.hidden = true;

    search.addEventListener("input", () => {
        const query = search.value.trim();
        tree.hidden = query !== "";
        results.hidden = query === "";
        if (query === "") return;
        void showResults(results, loaded, query, () => search.value.trim() === query, close);
    });
    page.append(search, tree, results);
    return page;
}

async function showResults(
    results: HTMLElement,
    loaded: Promise<PickerExample[]>[],
    query: string,
    current: () => boolean,
    close: () => void,
): Promise<void> {
    const all = (await Promise.allSettled(loaded)).flatMap((set) => (set.status === "fulfilled" ? set.value : []));
    if (!current()) return;
    const found = searchExamples(all, query);
    results.replaceChildren(
        ...(found.length === 0 ? [note("No examples match.")] : found.map((example) => renderExample(example, close))),
    );
}

/** A folder, drawn closed; its items are rendered the first time it is opened. */
function renderFolder(label: string, folder: Promise<ExampleFolder>, close: () => void): HTMLElement {
    const details = document.createElement("details");
    details.className = "idfweb-welcome-choice";
    const summary = document.createElement("summary");
    summary.className = "idfweb-welcome-entry";
    const title = document.createElement("span");
    title.className = "title";
    title.textContent = label;
    summary.append(title);
    const list = document.createElement("div");
    list.className = "idfweb-welcome-list";
    details.append(summary, list);

    folder.then(
        (arrived) => (title.textContent = `${label} (${arrived.count})`),
        () => {},
    );
    let rendered = false;
    details.addEventListener("toggle", () => {
        if (!details.open || rendered) return;
        rendered = true;
        const loading = note("Loading…");
        list.append(loading);
        folder.then(
            (arrived) =>
                list.replaceChildren(
                    ...arrived.items.map((item) =>
                        "items" in item
                            ? renderFolder(item.name, Promise.resolve(item), close)
                            : renderExample(item, close),
                    ),
                ),
            (e: unknown) => {
                loading.classList.add("failed");
                loading.textContent = e instanceof Error ? e.message : String(e);
            },
        );
    });
    return details;
}

/** One line with the path and the title; opened, the description, the chips and the button. */
function renderExample(example: PickerExample, close: () => void): HTMLElement {
    const details = document.createElement("details");
    details.className = "idfweb-example";
    const summary = document.createElement("summary");
    const path = document.createElement("span");
    path.className = "path";
    path.textContent = example.path;
    const title = document.createElement("span");
    title.className = "title";
    title.textContent = example.title;
    summary.append(path, title);
    summary.title = `${example.path} — ${example.title}`;

    const body = document.createElement("div");
    body.className = "body";
    if (example.description !== "") {
        const description = document.createElement("p");
        description.className = "description";
        description.append(renderInline(example.description));
        body.append(description);
    }
    if (example.detail !== undefined) {
        const detail = document.createElement("p");
        detail.className = "detail";
        detail.textContent = example.detail;
        body.append(detail);
    }
    const open = document.createElement("button");
    open.type = "button";
    open.className = "idfweb-example-open";
    open.textContent = "Open";
    open.addEventListener("click", () => {
        close();
        example.open();
    });
    body.append(open);
    details.append(summary, body);
    return details;
}

function note(text: string): HTMLElement {
    const p = document.createElement("p");
    p.className = "idfweb-welcome-note";
    p.textContent = text;
    return p;
}
